/**
 * Manual breakdown and Next Action (SPEC.md §8.6).
 *
 * A parent with subtasks renders folded by default: a fold marker, a progress count, and its
 * Next Action peeking through underneath — directly completable without unfolding (§8.6.4).
 * Unfolding reveals every subtask in `sort_order`, draggable, with the Next Action marked.
 *
 * The same "Break down" control creates a parent's first children or splits a subtask that
 * turned out too big in place (§8.6.2) — the server tells the two apart by whether the id it
 * was given already has a parent (§10), so nothing in here has to.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { Area, Project, Task, Tier } from "../api";
import type { TaskActions } from "./TaskList";
import { type AiTierAvailability, NO_AI_TIERS } from "../aiTiers";
import { AiBreakdownButton } from "./AiBreakdownButton";
import { EnergyBadgeButton } from "./EnergyBadgeButton";
import { TaskEditor } from "./TaskEditor";
import { Die } from "./Die";
import { Chevron, Flag, Pencil, Plus, Split, Undo } from "./icons";
import { useRowReorder } from "./useRowReorder";
import { energyTitle } from "./taskFormat";

/** §8.6.2: past this many subtasks, a quiet note suggests the parent may really be a
 * project. A suggestion only — nothing above it is blocked. */
const SUBTASK_PROJECT_HINT_THRESHOLD = 15;

/**
 * One title per line, enter to add (§8.6 §6). A separate submit commits the whole list at
 * once — creating (or splitting into) three rows one request at a time would leave a half
 * applied breakdown if the connection dropped partway through.
 */
export function BreakdownForm({
  onSubmit,
  onCancel,
  placeholder,
  submitLabel,
  replacing,
}: {
  onSubmit: (titles: string[]) => Promise<void>;
  onCancel: () => void;
  placeholder: string;
  submitLabel: string;
  /**
   * The step these will replace, when this is a split rather than a breakdown (§8.6.2).
   *
   * Named up front because the original is *deleted* — it was never done, so it is restated
   * rather than completed — and a row simply vanishing is alarming in a way the operation
   * itself is not. Reported exactly that way: "the panic sets in when it just vanishes."
   * Absent for a parent's own breakdown, where nothing is replaced: the task survives as the
   * parent of its new steps (D173).
   */
  replacing?: string;
}) {
  const [draft, setDraft] = useState("");
  const [titles, setTitles] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  function addDraft() {
    const t = draft.trim();
    if (!t) return;
    setTitles((cur) => [...cur, t]);
    setDraft("");
  }

  async function submit() {
    const all = draft.trim() ? [...titles, draft.trim()] : titles;
    if (all.length === 0) return;
    setBusy(true);
    try {
      await onSubmit(all);
      // Some callers (adding a step to an already-broken-down parent) leave this form
      // mounted afterward rather than closing it, so its own draft has to clear itself —
      // otherwise the titles just created would still be sitting here, looking unsaved.
      setTitles([]);
      setDraft("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="breakdown-form">
      {replacing !== undefined && (
        <p className="breakdown-replacing">
          {/* The colour is emphasis, not meaning — "becomes…" carries that on its own, so
              this still reads correctly without it. `--accent` rather than a literal blue:
              the dev scheme re-tints that token, and a hardcoded colour would belong to
              nothing there. */}
          <span className="replacing-title">“{replacing}”</span> becomes…
        </p>
      )}
      {titles.length > 0 && (
        <ul className="breakdown-draft">
          {titles.map((t, i) => (
            <li key={i}>
              <span>{t}</span>
              <button
                type="button"
                className="btn ghost small"
                onClick={() => setTitles((cur) => cur.filter((_, j) => j !== i))}
                aria-label={`Remove "${t}"`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      {/* No visible submit button — Enter is the only way to add a line, and a single-input
          form already submits on Enter without one. */}
      <form
        className="row wrap"
        onSubmit={(e) => {
          e.preventDefault();
          addDraft();
        }}
      >
        <input
          className="input"
          placeholder={placeholder}
          aria-label="Step title"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          autoFocus
        />
      </form>
      <div className="row wrap breakdown-confirm">
        <button
          type="button"
          className="btn primary small"
          disabled={busy || (titles.length === 0 && !draft.trim())}
          onClick={() => void submit()}
        >
          {busy ? "Saving…" : submitLabel}
        </button>
        <button type="button" className="btn ghost small" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/** The compact Next Action row shown beneath a folded parent — its own tick, no other
 * chrome, so it reads as a peek rather than a second full row (§8.6.4). */
function NextActionPeek({
  task,
  onComplete,
  justPromotedId,
}: {
  task: Task;
  onComplete: TaskActions["onComplete"];
  justPromotedId?: string | null;
}) {
  const [completing, setCompleting] = useState(false);
  const justPromoted = task.id === justPromotedId;

  // `completing` used to stick at `true` forever once set — nothing ever cleared it. That
  // was invisible for a top-level row (a completed one gets held in place, never reused for
  // a different task, until the actual page reload wipes the component along with it) but
  // this peek *does* get reused: the moment its subtask completes, the very same instance
  // re-renders showing whichever one silently became the new Next Action (§8.6.1). With the
  // stale flag still on, `.next-action-peek.completing`'s fade-to-invisible animation (meant
  // for the row that just finished) replayed on the brand new one instead, appearing to
  // "fly out and leave a blank" — reported directly. Resetting whenever the id changes is
  // safe for the row that legitimately triggered its own completion too: by the time the
  // reload lands the ~200ms fade already had its full window to play (the API call that
  // produces this render is itself deferred behind that same delay, below).
  useEffect(() => {
    setCompleting(false);
  }, [task.id]);

  function complete(event: React.MouseEvent<HTMLButtonElement>) {
    setCompleting(true);
    const box = event.currentTarget.closest(".next-action-peek")!.getBoundingClientRect();
    const at = { x: box.left + 20, y: box.top + box.height / 2 };
    window.setTimeout(() => void onComplete(task, at), 200);
  }

  return (
    <div
      className={`next-action-peek${completing ? " completing" : ""}${justPromoted ? " just-promoted" : ""}`}
    >
      <button
        className="tick"
        onClick={complete}
        disabled={completing}
        aria-label={`Complete: ${task.title}`}
        title="Complete this step"
      >
        {completing ? "✓" : ""}
      </button>
      <span className="title">{task.title}</span>
      <span className="badge energy">
        <Die value={task.energy} title={energyTitle(task.energy)} />
      </span>
    </div>
  );
}

/** One row in the unfolded list — tick, title, energy, the Next Action marker (or the
 * control to make it one), Break down (split), edit, and a drag handle. Deliberately not a
 * reused `TaskRow`: a subtask can't be starred, snoozed or dropped, and giving it the full
 * badge/action set would offer controls that only fail. */
function SubtaskItem({
  task,
  parent,
  index,
  areas,
  projects,
  actions,
  draggable,
  gripProps,
  rowProps,
  aiTiers = NO_AI_TIERS,
}: {
  task: Task;
  parent: Task;
  index: number;
  areas: Area[];
  projects: Project[];
  actions: TaskActions;
  draggable: boolean;
  gripProps?: (index: number) => Record<string, unknown>;
  rowProps?: (index: number) => Record<string, unknown>;
  aiTiers?: AiTierAvailability;
}) {
  const [completing, setCompleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [breakingDown, setBreakingDown] = useState(false);
  const justPromoted = task.id === actions.justPromotedId;
  const done = task.status === "done";

  // Nothing ever cleared `completing` once set, so undoing the same row later — status
  // flips back to `open` while the id stays the same — found it still `true` and replayed
  // the fade-to-invisible animation meant for the completion, not the undo. Reported
  // directly ("its row just went blank"). Keyed on `status` too, not just `id`: an undo
  // doesn't change which subtask this is, only whether it's done.
  useEffect(() => {
    setCompleting(false);
  }, [task.id, task.status]);
  const animating = completing && !done;
  const drag = draggable ? rowProps?.(index) : undefined;

  if (editing && actions.onEdit) {
    return (
      <TaskEditor
        task={task}
        areas={areas}
        projects={projects}
        onCancel={() => setEditing(false)}
        onSave={async (patch, recurrence) => {
          await actions.onEdit!(task, patch, recurrence);
          setEditing(false);
        }}
        // A step added in error had no way out at all — not on the row, not in here. It goes
        // where a task's delete already lives rather than becoming a seventh row icon
        // (§8.6.2, and §14 item 7 on how crowded these rows are getting).
        onDelete={actions.onDelete}
      />
    );
  }

  function complete(event: React.MouseEvent<HTMLButtonElement>) {
    setCompleting(true);
    const box = event.currentTarget.closest("li")!.getBoundingClientRect();
    const at = { x: box.left + 30, y: box.top + box.height / 2 };
    window.setTimeout(() => void actions.onComplete(task, at), 200);
  }

  return (
    <li
      {...drag}
      className={[
        "subtask-row",
        animating ? "completing" : "",
        done ? "done" : "",
        justPromoted ? "just-promoted" : "",
        drag && "className" in drag ? String(drag.className ?? "") : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-testid="subtask"
    >
      <button
        className="tick"
        onClick={done ? () => void actions.onUndo?.(task) : complete}
        disabled={animating || (done && !actions.onUndo)}
        aria-label={done ? `Undo: ${task.title}` : `Complete: ${task.title}`}
        title={done ? "Done. Click to put it back." : "Complete"}
      >
        {animating || done ? "✓" : ""}
      </button>

      <span className="title">
        <span className="text">{task.title}</span>
      </span>

      {!done && actions.onEdit ? (
        <EnergyBadgeButton
          task={task}
          onChange={(value) => actions.onEdit!(task, { energy: value })}
        />
      ) : (
        <span className="badge energy" title={energyTitle(task.energy)}>
          <Die value={task.energy} title={energyTitle(task.energy)} />
        </span>
      )}

      {!done &&
        (task.is_next_action ? (
          <span className="badge next-action" title="The current Next Action">
            <Flag filled /> Next
          </span>
        ) : (
          actions.onSetNextAction && (
            <button
              type="button"
              className="btn ghost small"
              onClick={() => void actions.onSetNextAction!(parent, task.id)}
              aria-label={`Make Next Action: ${task.title}`}
              title="Make this the Next Action"
            >
              <Flag />
            </button>
          )
        ))}

      {!done && actions.onEdit && (
        <button
          className="btn ghost small"
          onClick={() => setEditing(true)}
          aria-label={`Edit: ${task.title}`}
          title="Edit"
        >
          <Pencil />
        </button>
      )}

      {!done && actions.onBreakdown && (
        <button
          type="button"
          className="btn ghost small"
          onClick={() => setBreakingDown((v) => !v)}
          aria-label={`Replace with steps: ${task.title}`}
          title="This step is too big - replace it with smaller ones"
        >
          <Split />
        </button>
      )}
      {/* Held back until Split is pressed, same reasoning as the parent's own trigger in
          TaskList.tsx. */}
      {breakingDown && !done && actions.onAiBreakdown && (
        <AiBreakdownButton task={task} tiers={aiTiers} onRun={actions.onAiBreakdown} />
      )}

      {draggable && (
        <span className="grip" role="button" tabIndex={0} {...(gripProps?.(index) ?? {})}>
          ⠿
        </span>
      )}

      {breakingDown && actions.onBreakdown && (
        <div className="breakdown-wrap">
          <BreakdownForm
            placeholder="Replace with a step…"
            submitLabel="Confirm"
            replacing={task.title}
            onCancel={() => setBreakingDown(false)}
            onSubmit={async (titles) => {
              await actions.onBreakdown!(task, titles);
              setBreakingDown(false);
            }}
          />
        </div>
      )}
    </li>
  );
}

/** The fold/unfold block a `TaskRow` renders when its task has subtasks. */
/**
 * The breakdown just made, held so it can be taken back (§8.6.2) — until the next real load,
 * exactly like any other held row under §8.5: available for as long as the change is visible,
 * and no longer.
 *
 * `original` is set only for a *split*, which replaced a step as well as creating some;
 * breaking a parent down replaces nothing, so there is only a list of new steps to remove.
 */
export type UndoableBreakdown = { created: string[]; original?: Task } | null;

export function SubtaskFold({
  task,
  areas,
  projects,
  actions,
  undoable,
  setUndoable,
  aiTiers = NO_AI_TIERS,
}: {
  task: Task;
  areas: Area[];
  projects: Project[];
  actions: TaskActions;
  /** Owned by `TaskRow`, because a parent's own breakdown starts there and a split starts
   * here, and both undo through the same control. */
  undoable: UndoableBreakdown;
  setUndoable: (next: UndoableBreakdown) => void;
  aiTiers?: AiTierAvailability;
}) {
  const [unfolded, setUnfolded] = useState(false);
  const [addingSubtask, setAddingSubtask] = useState(false);

  // `useMemo`, not a plain `?? []`: the fallback would otherwise be a fresh array every
  // render, which is unstable as a `useCallback` dependency below.
  const subtasks = useMemo(() => task.subtasks ?? [], [task.subtasks]);
  const done = task.subtask_done_count ?? 0;
  const total = task.subtask_total_count ?? subtasks.length;
  const nextAction = subtasks.find((s) => s.id === task.next_action_id);

  const reorder = useCallback(
    (from: number, insertAt: number) => {
      if (!actions.onReorderSubtasks) return;
      // Same gap arithmetic as `AreasCard.reorder`: `insertAt` is a gap between rows, and
      // removing the dragged item first shifts every later gap down by one.
      const target = from < insertAt ? insertAt - 1 : insertAt;
      if (from === target) return;
      const next = [...subtasks];
      const [moved] = next.splice(from, 1);
      next.splice(target, 0, moved);
      void actions.onReorderSubtasks(task, next.map((s) => s.id));
    },
    [subtasks, actions, task],
  );
  const drag = useRowReorder(actions.onReorderSubtasks ? reorder : undefined, subtasks.length);

  /**
   * The steps see an `onBreakdown` that remembers what a *split* replaced. Wrapped here
   * rather than in each row because the ghost belongs to the list, not to the row that is
   * about to stop existing — and only for a subtask, since breaking down a parent replaces
   * nothing (§8.6.2).
   */
  const stepActions = useMemo(
    () => ({
      ...actions,
      onBreakdown: actions.onBreakdown
        ? async (target: Task, titles: string[]) => {
            const created = await actions.onBreakdown!(target, titles);
            if (created && created.length > 0) {
              setUndoable({
                created: created.map((c) => c.id),
                // A split names what it replaced; a parent's breakdown has nothing to name.
                original: target.parent_id ? target : undefined,
              });
            }
            return created;
          }
        : undefined,
      // Same undo bookkeeping as `onBreakdown` above, for AI-created/split steps (§11 item
      // 9b) — undo itself doesn't care which one made them (`services/subtasks.undo_breakdown`
      // is source-agnostic), only which ids to remove and what to restore.
      onAiBreakdown: actions.onAiBreakdown
        ? async (target: Task, tier: Tier) => {
            const created = await actions.onAiBreakdown!(target, tier);
            if (created && created.length > 0) {
              setUndoable({
                created: created.map((c) => c.id),
                original: target.parent_id ? target : undefined,
              });
            }
            return created;
          }
        : undefined,
    }),
    [actions, setUndoable],
  );

  // The hold is driven by the action having happened, not by the data having caught up —
  // the parent's steps refresh a moment later, so keying off "the original has gone" would
  // leave a window where neither the original nor its ghost is on screen. The original is
  // filtered out of the list below instead, so exactly one of the two is always shown.
  const replacedStep = undoable?.original;
  const steps = replacedStep
    ? subtasks.filter((sub) => sub.id !== replacedStep.id)
    : subtasks;

  const undoBreakdown = () => {
    if (!undoable || !actions.onUndoBreakdown) return;
    void actions.onUndoBreakdown(task, {
      created: undoable.created,
      restore: undoable.original
        ? {
            id: undoable.original.id,
            title: undoable.original.title,
            sortOrder: undoable.original.sort_order ?? 0,
          }
        : undefined,
    });
    setUndoable(null);
  };

  return (
    <div className="subtask-fold">
      {/* Fold toggle and the Next Action peek share one row — keeping the folded view to a
          single line is the point of folding at all; stacking them defeated it. */}
      <div className="subtask-fold-summary">
        <button
          type="button"
          className="btn ghost small fold-toggle"
          onClick={() => setUnfolded((v) => !v)}
          aria-expanded={unfolded}
          aria-label={`${unfolded ? "Fold" : "Unfold"} steps for ${task.title}`}
          title={unfolded ? "Fold" : "Unfold - see every step"}
        >
          <span className={`fold-chevron${unfolded ? " open" : ""}`} aria-hidden="true">
            <Chevron size={12} />
          </span>
          {done}/{total}
        </button>

        {/* Only once unfolded — a subtask can't be added to a row it can't be seen in, and
            the split-into-subtasks button already covers the folded parent's own case. */}
        {unfolded && actions.onBreakdown && (
          <button
            type="button"
            className="btn ghost small subtask-add-toggle"
            onClick={() => setAddingSubtask((v) => !v)}
            aria-expanded={addingSubtask}
            aria-label={`Add a step to ${task.title}`}
            title="Add a step"
          >
            <Plus size={12} />
          </button>
        )}
        {unfolded && actions.onAiBreakdown && (
          <AiBreakdownButton task={task} tiers={aiTiers} onRun={stepActions.onAiBreakdown!} />
        )}

        {undoable && !undoable.original && actions.onUndoBreakdown && (
          <button
            type="button"
            className="btn ghost small"
            aria-label={`Undo breaking down ${task.title}`}
            title="Take these steps back off"
            onClick={undoBreakdown}
          >
            <Undo size={14} />
          </button>
        )}

        {!unfolded && nextAction && (
          <NextActionPeek
            task={nextAction}
            onComplete={actions.onComplete}
            justPromotedId={actions.justPromotedId}
          />
        )}
      </div>

      {/* §8.6.2: "past roughly 15 subtasks, show a quiet note suggesting the parent may
          really be a project. Do not block anything." - a suggestion, not a limit, so it
          sits beside the count rather than in the way of adding a 16th step. */}
      {total > SUBTASK_PROJECT_HINT_THRESHOLD && (
        <p className="muted small subtask-project-hint">
          {total} steps - this might really be a project.
        </p>
      )}

      {unfolded && (
        <ul className="subtasks">
          {addingSubtask && stepActions.onBreakdown && (
            <li className="subtask-add-row">
              <BreakdownForm
                placeholder="A step…"
                submitLabel="Confirm"
                onCancel={() => setAddingSubtask(false)}
                onSubmit={async (titles) => {
                  // Left open rather than closed on submit (unlike a parent's own first
                  // breakdown): landing each new step at the top, right where this form
                  // sits, means another can follow immediately without reopening it — it
                  // closes only when the toggle that opened it is tapped again.
                  const created = await stepActions.onBreakdown!(task, titles);
                  if (created?.length && actions.onReorderSubtasks) {
                    await actions.onReorderSubtasks(task, [
                      ...created.map((c) => c.id),
                      ...subtasks.map((s) => s.id),
                    ]);
                  }
                }}
              />
            </li>
          )}
          {replacedStep && undoable && actions.onUndoBreakdown && (
            <li className="subtask replaced-step">
              <span className="title">
                <span className="replacing-title">“{replacedStep.title}”</span>
                <span className="muted"> · replaced by {undoable.created.length} steps</span>
              </span>
              <button
                type="button"
                className="btn ghost small"
                aria-label={`Put “${replacedStep.title}” back`}
                title="Put this step back, and remove the ones that replaced it"
                onClick={undoBreakdown}
              >
                <Undo size={14} />
              </button>
            </li>
          )}
          {steps.map((sub, index) => (
            <SubtaskItem
              key={sub.id}
              task={sub}
              parent={task}
              index={index}
              areas={areas}
              projects={projects}
              actions={stepActions}
              draggable={Boolean(actions.onReorderSubtasks)}
              gripProps={drag.gripProps}
              rowProps={drag.rowProps}
              aiTiers={aiTiers}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
